// Sound Effects Utility for WashPros
// Uses Web Audio API for crisp, instant sounds

class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.3; // Default volume (0-1)
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this;
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Set volume (0-1)
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }

  // Create oscillator-based sound
  playTone(frequency, duration, type = 'sine', fadeOut = true) {
    if (!this.enabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.value = this.volume;

      if (fadeOut) {
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          this.audioContext.currentTime + duration
        );
      }

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (e) { /* Audio API may be blocked by browser — non-critical */ }
  }

  // Hover sound - soft chime
  playHover() {
    this.playTone(880, 0.1, 'sine'); // A5 note
  }

  // Click sound - crisp tap
  playClick() {
    this.playTone(1200, 0.05, 'square');
  }

  // Success sound - happy chime
  playSuccess() {
    if (!this.enabled || !this.audioContext) return;
    
    // Play ascending notes
    setTimeout(() => this.playTone(523, 0.15, 'sine'), 0);    // C5
    setTimeout(() => this.playTone(659, 0.15, 'sine'), 100);  // E5
    setTimeout(() => this.playTone(784, 0.2, 'sine'), 200);   // G5
  }

  // Error sound - low buzz
  playError() {
    this.playTone(200, 0.2, 'sawtooth');
  }

  // Notification sound - gentle ping
  playNotification() {
    if (!this.enabled || !this.audioContext) return;
    
    this.playTone(1047, 0.1, 'sine'); // C6
    setTimeout(() => this.playTone(1319, 0.15, 'sine'), 80); // E6
  }

  // Whoosh sound for scrolling/transitions
  playWhoosh() {
    if (!this.enabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const filter = this.audioContext.createBiquadFilter();

      oscillator.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(100, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        2000,
        this.audioContext.currentTime + 0.1
      );

      filter.type = 'lowpass';
      filter.frequency.value = 1000;

      gainNode.gain.setValueAtTime(this.volume * 0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        this.audioContext.currentTime + 0.15
      );

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.15);
    } catch (e) { /* Audio API may be blocked by browser — non-critical */ }
  }

  // Sparkle sound - magical chime
  playSparkle() {
    if (!this.enabled || !this.audioContext) return;

    const notes = [1568, 1976, 2349, 2637]; // G6, B6, D7, E7
    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.15, 'sine');
      }, i * 50);
    });
  }

  // Water drop sound
  playWaterDrop() {
    if (!this.enabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1500, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        200,
        this.audioContext.currentTime + 0.3
      );

      gainNode.gain.setValueAtTime(this.volume * 0.5, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        this.audioContext.currentTime + 0.3
      );

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (e) { /* Audio API may be blocked by browser — non-critical */ }
  }

  // Car wash sound - swoosh
  playCarWash() {
    if (!this.enabled || !this.audioContext) return;

    try {
      // Create noise
      const bufferSize = this.audioContext.sampleRate * 0.3;
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = this.audioContext.createBufferSource();
      noise.buffer = buffer;

      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1000;
      filter.Q.value = 0.5;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(this.volume * 0.2, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      noise.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      noise.start(this.audioContext.currentTime);
    } catch (e) { /* Audio API may be blocked by browser — non-critical */ }
  }
}

// Create singleton instance
const soundEffects = new SoundEffects();

export default soundEffects;
export { SoundEffects };
